import MaintenanceNotice, { useMaintenance } from '../components/MaintenanceNotice';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../mock';
import { Mail, Lock, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const maintenance = useMaintenance();
  const { login, user, loading: authLoading } = useAuth();
  useEffect(() => {
    if (!authLoading && user?.role === 'admin') navigate('/portal', { replace: true });
  }, [authLoading, user?.role, navigate]);
  const { lang } = useLanguage();
  const t = translations[lang]?.auth || translations.en.auth;
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (maintenance) return;
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      if (response.data.requires_2fa) {
        navigate('/verify-2fa', { state: { email: formData.email, challenge: response.data.challenge, loginChallenge: true } });
      } else {
        login(response.data.access_token, response.data.user);
        navigate('/portal');
      }
    } catch (err) {
      if (err.response?.status === 403 && String(err.response?.data?.detail || '').startsWith('Account not verified')) {
        navigate('/verify-2fa', { state: { email: formData.email } });
      } else {
        setError(err.response?.data?.detail || t.loginFailed);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {maintenance && <MaintenanceNotice />}
          <div hidden={maintenance}>
          <div className="auth-header">
            <h1 className="auth-logo">KRYPTON9</h1>
            <h2 className="heading-2">{t.welcomeBack}</h2>
            <p className="body-medium text-secondary">{t.signInSubtitle}</p>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="body-small">{t.emailLabel}</label>
              <div className="input-with-icon">
                <Mail size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={t.emailPlaceholder}
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="body-small">{t.passwordLabel}</label>
              <div className="input-with-icon">
                <Lock size={18} />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary auth-btn" data-testid="login-submit-btn">
              {loading ? t.signingIn : t.signIn}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="auth-footer">
            <p className="body-small">
              {t.noAccount}{' '}
              <Link to="/register" className="auth-link">{t.createAccount}</Link>
            </p>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
